import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import { useModal } from '../components/useModal';

/**
 * Ambassador applications — people asking to represent a club or community
 * inside PLAY.
 *
 * Two decisions per row, and the old page treats them unevenly: declining asks
 * `window.confirm`, **approving asks nothing at all**. Approving grants a role;
 * it is the one that should be confirmed. Both are dialogs now, and both name the
 * applicant and their affiliation, because a list of eight columns is easy to
 * misclick by one row.
 *
 * The status filter is the old page's and the numbers behind it are its own map:
 * 0 pending, 1 approved, 2 declined. An unmapped number is shown as itself.
 */

const STATUS = { 0: 'Pending', 1: 'Approved', 2: 'Declined' };

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const AmbassadorApplicationsInternal = ({ api }) => {
  const { ask, modalProps } = useModal();

  const [status, setStatus] = useState('Pending');
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    setFailed(false);
    try {
      const data = await api.ambassadorApplications.list({ status, page: 1, count: 100 });
      const list = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
      setItems(list);
    } catch (error) {
      setFailed(true);
    }
  }, [api, status]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = (application, approving) => ask({
    kind: approving ? 'confirm' : 'danger',
    title: approving
      ? `Approve ${application.userName || 'this applicant'}?`
      : `Decline ${application.userName || 'this application'}?`,
    context: `${approving ? 'Approve' : 'Decline'} · ${application.playAffiliationName || 'no affiliation'}`,
    body: approving
      ? `${application.userName || 'They'} becomes an ambassador for ${application.playAffiliationName || 'their community'} and gets the role in PLAY.`
      : `${application.userName || 'They'} is told the application was not accepted. It can be submitted again.`,
    confirmLabel: approving ? 'Approve' : 'Decline',
    onConfirm: async () => {
      setBusy(true);
      try {
        if (approving) {
          await api.ambassadorApplications.approve(application.id);
        } else {
          await api.ambassadorApplications.decline(application.id);
        }
        setNote({ tone: 'info', text: approving ? 'Approved.' : 'Declined.' });
        await load();
      } catch (error) {
        setNote({
          tone: 'error',
          // 409 { reason: "not_pending", status } when it was already decided.
          text: `Unable to ${approving ? 'approve' : 'decline'}: ${error?.payload?.responseData?.reason === 'not_pending'
            ? `the application is no longer pending (${error.payload.responseData.status})`
            : (error?.message || 'unknown error')}`
        });
        throw error;
      } finally {
        setBusy(false);
      }
    }
  });

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Ambassador applications</h1>
          <p className="next-muted">
            {items === null ? 'Loading…' : `${items.length} ${status.toLowerCase()}`}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-card">
        <div className="next-filters__row">
          <label className="next-filter">
            <span className="next-filter__label">Status</span>
            <select
              className="next-input"
              value={status}
              onChange={event => setStatus(event.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Declined">Declined</option>
            </select>
          </label>
        </div>
      </section>

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the applications.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Community</th>
                  <th>Sport</th>
                  <th>Contribution note</th>
                  <th>Submitted</th>
                  <th>Decided</th>
                  <th>Status</th>
                  <th className="next-table__right">Decision</th>
                </tr>
              </thead>
              <tbody>
                {items === null && (
                  <tr><td colSpan={8} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {items !== null && items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="next-muted next-table__note">
                      No {status.toLowerCase()} application.
                    </td>
                  </tr>
                )}
                {(items ?? []).map(application => (
                  <tr key={application.id}>
                    <td data-label="">
                      <span className="next-stack">
                        <span className="next-stack__main">{application.userName || '—'}</span>
                        <span className="next-stack__sub next-break">
                          {[application.userEmail, application.userPhone].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </td>
                    <td data-label="Community">
                      <span className="next-stack">
                        <span>{application.playAffiliationName || '—'}</span>
                        {application.playAffiliationKind && (
                          <span className="next-stack__sub">{application.playAffiliationKind}</span>
                        )}
                      </span>
                    </td>
                    <td data-label="Sport">{application.sportCategory || <span className="next-muted">—</span>}</td>
                    <td data-label="Contribution note">
                      {application.contributionNote || <span className="next-muted">—</span>}
                    </td>
                    <td data-label="Submitted" className="next-nowrap next-muted">{fmtDate(application.createdAtUtc)}</td>
                    <td data-label="Decided" className="next-nowrap next-muted">{fmtDate(application.decidedAtUtc)}</td>
                    <td data-label="Status">
                      <StatusPill status={STATUS[application.status] || String(application.status)}>
                        {STATUS[application.status] || `Status ${application.status}`}
                      </StatusPill>
                    </td>
                    <td className="next-table__right" data-label="">
                      {application.status === 0 ? (
                        <div className="next-actions next-actions--end">
                          <button
                            type="button"
                            className="next-btn next-btn--sm next-btn--primary"
                            disabled={busy}
                            onClick={() => decide(application, true)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="next-btn next-btn--sm next-btn--danger"
                            disabled={busy}
                            onClick={() => decide(application, false)}
                          >
                            Decline
                          </button>
                        </div>
                      ) : (
                        <span className="next-muted">Decided {fmtDate(application.decidedAtUtc)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="next-muted next-page__footnote">
        Players and their data are exported from <Link className="next-link" to="/next/play">Play</Link>.
      </p>

      <Modal {...modalProps} />
    </div>
  );
};

AmbassadorApplicationsInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const AmbassadorApplications = withApi(AmbassadorApplicationsInternal);

AmbassadorApplications.requiresLogin = true;

export default AmbassadorApplications;
